// Brumby Brew — theme.js (foundation layer)
// Owns: motion gate + scroll-entrance engine (04-brand-system §6), cart API,
// global cart drawer (03-ia.md §3.4), quick-add sheet (03-ia.md §2.5, F-205 fix),
// header cart badge, legacy FAQ/brewing-guide support for not-yet-rebuilt sections.
//
// Rules enforced here:
// - Reduced-motion kill layer 2 of 2: anime.js and the IntersectionObserver are
//   never initialized when prefers-reduced-motion is set; animated elements get
//   their final resting state immediately. (Layer 1 is CSS in theme.liquid.)
// - Zero location.reload() for drawer line updates (F-303).
// - Digits never tween — prices, counts and subtotals swap, containers may move.
// - Cart drawer opens on EVERY add (F-108); the drawer is the confirmation.

(function () {
  'use strict';

  /* ════════════════════════════════════════════════════════════
     Motion gate — checked ONCE at boot (04-brand-system §6)
     ════════════════════════════════════════════════════════════ */
  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var Brumby = window.BrumbyBrew || {};
  Brumby.reducedMotion = REDUCED;
  // anime.js v4 namespace (window.anime = { animate, stagger, spring, ... }).
  // null under reduced motion — section scripts MUST check this gate, never window.anime.
  Brumby.anime = null;

  /* ════════════════════════════════════════════════════════════
     Utilities
     ════════════════════════════════════════════════════════════ */
  function formatPrice(cents) {
    var value = (Math.round(cents) / 100).toFixed(2);
    return '$' + value;
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = text;
    return node;
  }

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  // Only elements that are actually rendered can be trap boundaries — the cart
  // drawer keeps [hidden] footer/empty blocks in the DOM, and a hidden "last"
  // element would let Tab escape behind the open dialog.
  function visibleFocusables(container) {
    return Array.prototype.filter.call(
      container.querySelectorAll(FOCUSABLE),
      function (node) { return node.getClientRects().length > 0; }
    );
  }

  function trapFocus(container) {
    function handleKeydown(e) {
      if (e.key !== 'Tab') return;
      var focusables = visibleFocusables(container);
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      // Shift+Tab from the first control OR from the panel itself (which holds
      // tabindex="-1" focus right after opening) must wrap to the last control —
      // otherwise focus escapes behind the dialog.
      if (e.shiftKey && (document.activeElement === first || document.activeElement === container)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    container.addEventListener('keydown', handleKeydown);
    return function release() {
      container.removeEventListener('keydown', handleKeydown);
    };
  }

  // Error feedback only — success feedback is the cart drawer, never a toast.
  function showNotification(message) {
    var existing = document.querySelector('.brumby-toast');
    if (existing) existing.remove();
    var toast = el('div', 'brumby-toast');
    toast.setAttribute('role', 'status');
    document.body.appendChild(toast);
    // Live regions announce CHANGES — insert empty, then populate, so screen
    // readers reliably pick the message up.
    window.requestAnimationFrame(function () { toast.textContent = message; });
    setTimeout(function () {
      toast.classList.add('brumby-toast--leaving');
      setTimeout(function () { toast.remove(); }, 250);
    }, 3200);
  }

  /* ════════════════════════════════════════════════════════════
     Cart API — /cart/*.js only, no page reloads in the drawer path
     ════════════════════════════════════════════════════════════ */
  function fetchCart() {
    return fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  function addToCart(variantId, quantity, btn) {
    quantity = quantity || 1;
    if (btn) {
      if (btn._atcInProgress) return Promise.resolve();
      btn._atcInProgress = true;
      btn.disabled = true;
    }
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: quantity })
    })
      .then(function (response) {
        if (!response.ok) throw new Error('add failed');
        return fetchCart();
      })
      .then(function (cart) {
        renderCart(cart);
        openCartDrawer({ confirm: true });
        bounceCartBadge();
        return cart;
      })
      .catch(function (err) {
        console.error(err);
        showNotification('Couldn’t add that. Try again.');
      })
      .finally(function () {
        if (btn) {
          btn._atcInProgress = false;
          btn.disabled = false;
        }
      });
  }

  // Change by line-item key (drawer path — F-303: no reload).
  function changeLineByKey(key, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('change failed');
        return r.json();
      })
      .then(function (cart) {
        renderCart(cart);
        return cart;
      })
      .catch(function (err) {
        console.error(err);
        showNotification('Couldn’t update the cart. Try again.');
      });
  }

  // Legacy signature used by the /cart page section (out of this phase's scope):
  // that page's markup is server-rendered, so it still reloads to stay in sync.
  function updateCartItem(line, quantity) {
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity })
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (window.location.pathname.indexOf('/cart') === 0) {
          window.location.reload();
        } else {
          renderCart(cart);
        }
      })
      .catch(function (err) {
        console.error(err);
        showNotification('Couldn’t update the cart. Try again.');
      });
  }

  function updateCartCount() {
    fetchCart().then(function (cart) {
      updateBadge(cart.item_count);
    });
  }

  function updateBadge(itemCount) {
    var badge = document.querySelector('.header__cart-count, [data-cart-count]');
    if (badge) {
      badge.textContent = itemCount;
      badge.style.display = itemCount > 0 ? '' : 'none';
    }
    var cartLink = document.querySelector('[data-cart-drawer-open], .header__cart-link, a[href="/cart"]');
    if (cartLink) {
      var itemsWord = itemCount === 1 ? 'item' : 'items';
      cartLink.setAttribute('aria-label', itemCount > 0 ? 'Cart, ' + itemCount + ' ' + itemsWord : 'Cart, empty');
    }
  }

  // Container bounces on add (spring, ≤400ms); the number itself just swaps.
  function bounceCartBadge() {
    if (REDUCED) return;
    var badge = document.querySelector('.header__cart-count, [data-cart-count]');
    if (!badge) return;
    badge.classList.remove('is-bouncing');
    // restart the animation
    void badge.offsetWidth;
    badge.classList.add('is-bouncing');
    badge.addEventListener('animationend', function handler() {
      badge.classList.remove('is-bouncing');
      badge.removeEventListener('animationend', handler);
    });
  }

  /* ════════════════════════════════════════════════════════════
     Cart drawer — 03-ia.md §3.4
     ════════════════════════════════════════════════════════════ */
  var drawer = {
    root: null,
    panel: null,
    items: null,
    releaseTrap: null,
    lastFocus: null,
    confirmTimer: null
  };

  function initCartDrawer() {
    drawer.root = document.querySelector('[data-cart-drawer]');
    if (!drawer.root) return;
    drawer.panel = drawer.root.querySelector('.cart-drawer__panel');
    drawer.items = drawer.root.querySelector('[data-cart-drawer-items]');

    drawer.root.querySelectorAll('[data-cart-drawer-close]').forEach(function (target) {
      target.addEventListener('click', closeCartDrawer);
    });

    // Recommendation quick-adds (delegated — rows toggle with cart state)
    drawer.root.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-rec-add]');
      if (!btn) return;
      addToCart(btn.dataset.variantId, 1, btn);
    });

    // Delegated qty / remove controls
    drawer.items.addEventListener('click', function (e) {
      var item = e.target.closest('[data-cart-item]');
      if (!item || item.dataset.busy === 'true') return;
      var key = item.dataset.lineKey;
      var qtyEl = item.querySelector('[data-qty-value]');
      var qty = qtyEl ? parseInt(qtyEl.textContent, 10) || 0 : 0;

      var action = null;
      var controlSelector = null;
      if (e.target.closest('[data-qty-minus]')) { action = qty - 1; controlSelector = '[data-qty-minus]'; }
      else if (e.target.closest('[data-qty-plus]')) { action = qty + 1; controlSelector = '[data-qty-plus]'; }
      else if (e.target.closest('[data-line-remove]')) { action = 0; controlSelector = null; }
      if (action === null) return;

      item.dataset.busy = 'true';
      changeLineByKey(key, Math.max(0, action)).finally(function () {
        // renderCart rebuilt the list — the pressed button is gone from the DOM.
        // Keyboard users must not be dumped to <body> (outside the focus trap):
        // re-focus the same control on the rebuilt row, or the panel as fallback.
        if (!drawer.panel || !drawer.panel.contains(document.activeElement)) {
          var newRow = controlSelector && drawer.items
            ? drawer.items.querySelector('[data-cart-item][data-line-key="' + key + '"]')
            : null;
          var newControl = newRow ? newRow.querySelector(controlSelector) : null;
          if (newControl) {
            newControl.focus();
          } else if (drawer.root.classList.contains('is-open')) {
            drawer.panel.focus();
          }
        }
      });
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.root.classList.contains('is-open')) closeCartDrawer();
    });

    // Any element (e.g. the header cart icon) can opt in with data-cart-drawer-open.
    document.addEventListener('click', function (e) {
      var opener = e.target.closest('[data-cart-drawer-open]');
      if (!opener) return;
      e.preventDefault();
      fetchCart().then(renderCart);
      openCartDrawer({ confirm: false });
    });
  }

  function openCartDrawer(options) {
    if (!drawer.root) return;
    options = options || {};
    drawer.lastFocus = document.activeElement;

    // MOTION: drawer-open pop — overshoot easing ONLY when the drawer opens as
    // add-to-cart confirmation (04 §6 cart row). The class lands before .is-open
    // so the easing applies to THIS slide-in. Never under reduced motion.
    drawer.root.classList.toggle('cart-drawer--pop', !REDUCED && !!options.confirm);

    drawer.root.classList.add('is-open');
    drawer.root.setAttribute('aria-hidden', 'false');
    document.body.classList.add('drawer-lock');

    var confirmation = drawer.root.querySelector('[data-cart-drawer-confirmation]');
    if (confirmation) {
      clearTimeout(drawer.confirmTimer);
      if (options.confirm) {
        confirmation.hidden = false;
        drawer.confirmTimer = setTimeout(function () { confirmation.hidden = true; }, 4000);
      } else {
        confirmation.hidden = true;
      }
    }

    if (drawer.releaseTrap) drawer.releaseTrap();
    drawer.releaseTrap = trapFocus(drawer.panel);
    drawer.panel.focus();
  }

  function closeCartDrawer() {
    if (!drawer.root || !drawer.root.classList.contains('is-open')) return;
    drawer.root.classList.remove('is-open');
    drawer.root.classList.remove('cart-drawer--pop'); // pop is per-add, never sticky
    drawer.root.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('drawer-lock');
    if (drawer.releaseTrap) {
      drawer.releaseTrap();
      drawer.releaseTrap = null;
    }
    if (drawer.lastFocus && document.contains(drawer.lastFocus)) drawer.lastFocus.focus();
  }

  function renderCart(cart) {
    updateBadge(cart.item_count);
    if (!drawer.root) return;

    var count = drawer.root.querySelector('[data-cart-drawer-count]');
    if (count) count.textContent = cart.item_count > 0 ? '(' + cart.item_count + ')' : '';

    var subtotal = drawer.root.querySelector('[data-cart-drawer-subtotal]');
    if (subtotal) subtotal.textContent = formatPrice(cart.total_price);

    var empty = drawer.root.querySelector('[data-cart-drawer-empty]');
    var footer = drawer.root.querySelector('[data-cart-drawer-footer]');
    if (empty) empty.hidden = cart.item_count > 0;
    if (footer) footer.hidden = cart.item_count === 0;

    renderShippingBar(cart);
    renderRecs(cart);
    renderLineItems(cart);
  }

  function renderShippingBar(cart) {
    var bar = drawer.root.querySelector('[data-shipping-bar]');
    if (!bar) return;
    var threshold = parseInt(drawer.root.dataset.thresholdCents, 10) || 0;
    if (threshold <= 0 || cart.item_count === 0) {
      bar.hidden = true;
      return;
    }
    bar.hidden = false;
    var remaining = threshold - cart.total_price;
    var label = bar.querySelector('[data-shipping-label]');
    var fill = bar.querySelector('[data-shipping-fill]');
    var unlocked = remaining <= 0;
    bar.classList.toggle('is-unlocked', unlocked);
    if (unlocked) {
      if (label) label.textContent = '✓ Free shipping unlocked';
      if (fill) fill.style.width = '100%';
    } else {
      if (label) label.textContent = "You're " + formatPrice(remaining) + ' from free shipping';
      if (fill) fill.style.width = Math.min(100, (cart.total_price / threshold) * 100) + '%';
    }
  }

  /* Pre-checkout recommendations: hide picks already in the cart; hide the
     block entirely when the cart is empty or nothing is left to suggest. */
  function renderRecs(cart) {
    var wrap = drawer.root.querySelector('[data-cart-recs]');
    if (!wrap) return;
    var inCart = cart.items.map(function (item) { return String(item.product_id); });
    var visible = 0;
    wrap.querySelectorAll('[data-cart-rec]').forEach(function (row) {
      var hide = inCart.indexOf(row.dataset.productId) !== -1;
      row.hidden = hide;
      if (!hide) visible++;
    });
    wrap.hidden = cart.item_count === 0 || visible === 0;
  }

  function renderLineItems(cart) {
    if (!drawer.items) return;
    drawer.items.textContent = '';

    cart.items.forEach(function (item) {
      var row = el('div', 'cart-item');
      row.setAttribute('data-cart-item', '');
      row.dataset.lineKey = item.key;

      var media = el('a', 'cart-item__media');
      media.href = item.url;
      media.tabIndex = -1;
      media.setAttribute('aria-hidden', 'true');
      if (item.image) {
        var img = document.createElement('img');
        img.src = item.image;
        img.alt = '';
        img.loading = 'lazy';
        img.width = 80;
        img.height = 80;
        media.appendChild(img);
      }
      row.appendChild(media);

      var info = el('div', 'cart-item__info');
      var title = el('p', 'cart-item__title');
      var titleLink = el('a', null, item.product_title);
      titleLink.href = item.url;
      title.appendChild(titleLink);
      info.appendChild(title);

      if (item.variant_title && !item.product_has_only_default_variant) {
        info.appendChild(el('p', 'cart-item__variant', item.variant_title));
      }

      var controls = el('div', 'cart-item__controls');
      var qty = el('div', 'cart-item__qty');
      var minus = el('button', 'cart-item__qty-btn', '−');
      minus.type = 'button';
      minus.setAttribute('data-qty-minus', '');
      minus.setAttribute('aria-label', 'Decrease quantity');
      var value = el('span', 'cart-item__qty-value', String(item.quantity));
      value.setAttribute('data-qty-value', '');
      value.setAttribute('aria-live', 'polite');
      var plus = el('button', 'cart-item__qty-btn', '+');
      plus.type = 'button';
      plus.setAttribute('data-qty-plus', '');
      plus.setAttribute('aria-label', 'Increase quantity');
      qty.appendChild(minus);
      qty.appendChild(value);
      qty.appendChild(plus);
      controls.appendChild(qty);

      var remove = el('button', 'cart-item__remove', 'Remove');
      remove.type = 'button';
      remove.setAttribute('data-line-remove', '');
      controls.appendChild(remove);
      info.appendChild(controls);
      row.appendChild(info);

      row.appendChild(el('p', 'cart-item__price', formatPrice(item.final_line_price)));
      drawer.items.appendChild(row);
    });
  }

  /* ════════════════════════════════════════════════════════════
     Quick-add sheet — 03-ia.md §2.5 (fixes F-205: one pill group
     per REAL option axis, never variant-title string parsing)
     ════════════════════════════════════════════════════════════ */
  var quickAdd = {
    root: null,
    panel: null,
    product: null,
    selected: [],
    releaseTrap: null,
    lastFocus: null
  };

  function initQuickAdd() {
    quickAdd.root = document.querySelector('[data-quick-add-sheet]');

    // Card grind toggle (v3 draft 4e): selecting a pill retargets the card's
    // quick-add button at that variant. Delegated so it works on every card
    // surface with zero wiring.
    document.addEventListener('click', function (e) {
      var pill = e.target.closest('[data-card-grind-pill]');
      if (!pill || pill.disabled) return;
      e.preventDefault();
      e.stopPropagation();
      var card = pill.closest('[data-product-card]');
      if (!card) return;
      card.querySelectorAll('[data-card-grind-pill]').forEach(function (p) {
        p.classList.remove('is-selected');
        p.setAttribute('aria-pressed', 'false');
      });
      pill.classList.add('is-selected');
      pill.setAttribute('aria-pressed', 'true');
      var qa = card.querySelector('[data-quick-add]');
      if (qa) qa.dataset.variantId = pill.dataset.variantId;
    });

    // Delegated trigger — whole card is tappable, quick-add stops propagation.
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-quick-add]');
      if (!btn || btn.disabled) return;
      e.preventDefault();
      e.stopPropagation();

      if (btn.dataset.singleVariant === 'true') {
        addToCart(parseInt(btn.dataset.variantId, 10), 1, btn);
        return;
      }

      var data;
      try {
        data = JSON.parse(btn.dataset.quickAddProduct || 'null');
      } catch (err) {
        data = null;
      }
      if (!data || !data.variants || !data.variants.length) return;

      if (data.variants.length === 1) {
        addToCart(data.variants[0].id, 1, btn);
        return;
      }
      openQuickAdd(data, btn);
    });

    if (!quickAdd.root) return;
    quickAdd.panel = quickAdd.root.querySelector('.quick-add__panel');

    quickAdd.root.querySelectorAll('[data-quick-add-close]').forEach(function (target) {
      target.addEventListener('click', closeQuickAdd);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && quickAdd.root.classList.contains('is-open')) closeQuickAdd();
    });

    var atc = quickAdd.root.querySelector('[data-quick-add-atc]');
    atc.addEventListener('click', function () {
      var variant = matchedVariant();
      if (!variant || !variant.available) return;
      closeQuickAdd();
      addToCart(variant.id, 1);
    });

    // Tapping the disabled ATC: name the first missing axis (06-copy-deck §2.5).
    quickAdd.root.querySelector('.quick-add__footer').addEventListener('pointerdown', function () {
      if (!atc.disabled || !quickAdd.product) return;
      var missingIndex = quickAdd.selected.indexOf(null);
      if (missingIndex > -1) {
        setHelper('Pick a ' + quickAdd.product.options[missingIndex].name.toLowerCase() + ' to continue');
      }
    });
  }

  function setHelper(message) {
    var helper = quickAdd.root.querySelector('[data-quick-add-helper]');
    if (!helper) return;
    if (message) {
      helper.textContent = message;
      helper.hidden = false;
    } else {
      helper.textContent = '';
      helper.hidden = true;
    }
  }

  function openQuickAdd(product, triggerBtn) {
    if (!quickAdd.root) return;
    quickAdd.product = product;
    quickAdd.selected = product.options.map(function (option) {
      return option.values.length === 1 ? option.values[0] : null;
    });
    quickAdd.lastFocus = triggerBtn || document.activeElement;

    quickAdd.root.querySelector('[data-quick-add-title]').textContent = product.title;
    setHelper(null);
    buildOptionGroups();
    refreshQuickAddState();

    // Desktop ≥1024 with a pointer: anchored popover on the card; otherwise bottom sheet.
    var popover = window.matchMedia('(min-width: 1024px) and (hover: hover)').matches && triggerBtn;
    quickAdd.root.classList.toggle('quick-add--popover', !!popover);
    if (popover) {
      var rect = triggerBtn.getBoundingClientRect();
      var panelWidth = 320;
      var left = Math.max(16, Math.min(rect.right - panelWidth, window.innerWidth - panelWidth - 16));
      quickAdd.panel.style.left = left + 'px';
      quickAdd.panel.style.top = Math.min(rect.bottom + 8, window.innerHeight - 100) + 'px';
    } else {
      quickAdd.panel.style.left = '';
      quickAdd.panel.style.top = '';
    }

    quickAdd.root.classList.add('is-open');
    quickAdd.root.setAttribute('aria-hidden', 'false');
    document.body.classList.add('drawer-lock');
    if (quickAdd.releaseTrap) quickAdd.releaseTrap();
    quickAdd.releaseTrap = trapFocus(quickAdd.panel);
    quickAdd.panel.focus();
  }

  function closeQuickAdd() {
    if (!quickAdd.root || !quickAdd.root.classList.contains('is-open')) return;
    quickAdd.root.classList.remove('is-open');
    quickAdd.root.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('drawer-lock');
    if (quickAdd.releaseTrap) {
      quickAdd.releaseTrap();
      quickAdd.releaseTrap = null;
    }
    if (quickAdd.lastFocus && document.contains(quickAdd.lastFocus)) quickAdd.lastFocus.focus();
    quickAdd.product = null;
  }

  function buildOptionGroups() {
    var container = quickAdd.root.querySelector('[data-quick-add-options]');
    container.textContent = '';

    quickAdd.product.options.forEach(function (option, axisIndex) {
      var group = el('fieldset', 'quick-add__group');
      var legend = el('legend', 'quick-add__group-label', option.name);
      group.appendChild(legend);

      var pills = el('div', 'quick-add__pills');
      option.values.forEach(function (value) {
        var pill = el('button', 'quick-add__pill', value);
        pill.type = 'button';
        pill.dataset.axis = String(axisIndex);
        pill.dataset.value = value;
        pill.addEventListener('click', function () {
          if (pill.classList.contains('quick-add__pill--unavailable')) {
            setHelper('Out of stock in this combo');
            return;
          }
          setHelper(null);
          quickAdd.selected[axisIndex] = quickAdd.selected[axisIndex] === value ? null : value;
          refreshQuickAddState();
        });
        pills.appendChild(pill);
      });
      group.appendChild(pills);
      container.appendChild(group);
    });
  }

  function matchedVariant() {
    if (!quickAdd.product || quickAdd.selected.indexOf(null) > -1) return null;
    var match = null;
    quickAdd.product.variants.forEach(function (variant) {
      var fits = variant.options.every(function (value, i) {
        return value === quickAdd.selected[i];
      });
      if (fits) match = variant;
    });
    return match;
  }

  // A value is unavailable when NO available variant carries it alongside the
  // current selections on the OTHER axes.
  function valueAvailable(axisIndex, value) {
    return quickAdd.product.variants.some(function (variant) {
      if (!variant.available) return false;
      if (variant.options[axisIndex] !== value) return false;
      return quickAdd.selected.every(function (selectedValue, i) {
        if (i === axisIndex || selectedValue === null) return true;
        return variant.options[i] === selectedValue;
      });
    });
  }

  function refreshQuickAddState() {
    quickAdd.root.querySelectorAll('.quick-add__pill').forEach(function (pill) {
      var axisIndex = parseInt(pill.dataset.axis, 10);
      var value = pill.dataset.value;
      var selected = quickAdd.selected[axisIndex] === value;
      var available = valueAvailable(axisIndex, value);
      pill.classList.toggle('quick-add__pill--selected', selected);
      pill.classList.toggle('quick-add__pill--unavailable', !available);
      pill.setAttribute('aria-pressed', selected ? 'true' : 'false');
      if (!available) pill.setAttribute('aria-disabled', 'true');
      else pill.removeAttribute('aria-disabled');
    });

    var atc = quickAdd.root.querySelector('[data-quick-add-atc]');
    var price = quickAdd.root.querySelector('[data-quick-add-price]');
    var variant = matchedVariant();
    if (variant && variant.available) {
      atc.disabled = false;
      if (price) price.textContent = formatPrice(variant.price);
    } else {
      atc.disabled = true;
      if (price) price.textContent = '';
      if (variant && !variant.available) setHelper('Out of stock in this combo');
    }
  }

  /* ════════════════════════════════════════════════════════════
     Scroll-entrance engine — one IntersectionObserver, animate
     ONCE, transforms/opacity only (04-brand-system §6).
     Sections opt in with data-animate; grids add
     data-animate-stagger to the container (60ms steps, capped at
     6 — items 7+ render with no delay).
     ════════════════════════════════════════════════════════════ */
  function initEntrances() {
    var targets = document.querySelectorAll('[data-animate]');
    // The Goods renders preloaded (operator 2026-07-15) — its cards never
    // join the entrance observer, so no opacity:0 flash and no fade.
    var legacyTargets = Array.prototype.filter.call(
      document.querySelectorAll('.feature-card, .product-card, .faq-item, .taste-profile__card'),
      function (el) { return !el.closest('[data-section-type="the-goods"]'); }
    );

    if (REDUCED) {
      // Final resting state, no observers, no anime.
      targets.forEach(function (target) { target.classList.add('is-inview'); });
      legacyTargets.forEach(function (target) { target.classList.add('animate-fade-in'); });
      return;
    }

    Brumby.anime = window.anime || null;

    // Shopping grids reveal FAST (operator 2026-07-15): tighter stagger here,
    // shorter duration + pre-viewport trigger below — the next product is
    // already visible by the time the scroll lands on it.
    var FAST_GRIDS = '.clx__grid, .pdp__related-grid';

    document.querySelectorAll('[data-animate-stagger]').forEach(function (group) {
      var step = group.matches(FAST_GRIDS) ? 25 : 60;
      Array.prototype.forEach.call(group.children, function (child, i) {
        var delay = i < 6 ? i * step : 0;
        child.style.setProperty('--stagger-delay', delay + 'ms');
      });
    });

    function onIntersect(entries, obs) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-inview');
        entry.target.classList.add('animate-fade-in'); // legacy class, same beat
        obs.unobserve(entry.target);
      });
    }

    // Fire as soon as any part of the element is within ~22% below the fold, so
    // content is already fading in before you scroll to it — no dead space
    // waiting at the viewport edge (operator 2026-07-15).
    var observer = new IntersectionObserver(onIntersect, { threshold: 0, rootMargin: '0px 0px 22% 0px' });
    // Grids reveal even earlier.
    var eagerObserver = new IntersectionObserver(onIntersect, { threshold: 0.01, rootMargin: '0px 0px 35% 0px' });

    targets.forEach(function (target) {
      (target.closest(FAST_GRIDS) ? eagerObserver : observer).observe(target);
    });

    // Legacy sections (pre-rebuild): keep their fade-up working.
    document.querySelectorAll('.features-grid, .products-grid, .taste-profile__grid').forEach(function (grid) {
      grid.querySelectorAll('.feature-card, .product-card, .taste-profile__card').forEach(function (card, i) {
        card.style.setProperty('--stagger-delay', (i < 6 ? i * 60 : 0) + 'ms');
      });
    });
    legacyTargets.forEach(function (target) {
      target.classList.add('will-animate');
      (target.closest(FAST_GRIDS) ? eagerObserver : observer).observe(target);
    });
  }

  /* ════════════════════════════════════════════════════════════
     MOTION MODULE — anime.js v4 delight layer (04-brand-system §6)

     Owns the two anime-driven moments: hero word spring + Brumby
     peek-in. Everything else motion-related lives elsewhere by
     design: scroll entrances = initEntrances above (product cards,
     benefit chips, review cards), sticker wiggle = pure CSS
     (.sticker--wiggle), marquee ticker = pure CSS, cart badge
     bounce = bounceCartBadge above, drawer pop = openCartDrawer.

     Rules enforced here:
     - transforms + opacity ONLY; nothing here touches layout.
     - Reduced motion: REDUCED short-circuits every path (layer 2
       of the two-layer kill; layer 1 is CSS in theme.liquid).
     - LCP-safe: hero words are hidden pre-JS ONLY behind the
       html.bb-motion gate (inline script in theme.liquid, pre-paint,
       never under reduced motion), with a 2.5s CSS failsafe reveal.
     - Defensive: missing elements, missing/broken anime.js, missing
       IntersectionObserver → silent no-op or CSS-transition fallback.
       Digits, prices and ratings are never targeted.
     ════════════════════════════════════════════════════════════ */

  // anime v4 namespace — but ONLY when motion is allowed and the CDN delivered.
  function motionEngine() {
    if (REDUCED) return null;
    var A = window.anime;
    if (A && typeof A.animate === 'function' && typeof A.stagger === 'function') return A;
    return null;
  }

  // spring(mass 1, stiffness, damping, velocity 0) with a graceful fallback if
  // the pinned build ever renames createSpring.
  function springEase(A, stiffness, damping) {
    try {
      if (typeof A.createSpring === 'function') {
        return A.createSpring({ mass: 1, stiffness: stiffness, damping: damping, velocity: 0 });
      }
    } catch (e) { /* fall through */ }
    return 'outBack'; // closest built-in overshoot
  }

  // CSS-transition reveal on the foundation beat (550ms ease-out) — used when
  // anime.js is unavailable but motion is allowed. Transforms/opacity only.
  function cssReveal(target, delayMs) {
    target.style.transition =
      'opacity var(--dur-entrance) var(--ease-out) ' + delayMs + 'ms, ' +
      'transform var(--dur-entrance) var(--ease-out) ' + delayMs + 'ms';
    // Double rAF: the hidden state must paint before we release it.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        target.style.opacity = '1';
        target.style.transform = 'none';
      });
    });
  }

  /* Hero headline — word-by-word spring-in on load: spring(1, 80, 12, 0),
     60ms stagger, compressed when the headline runs long so the last word
     still STARTS by ~420ms and the whole sequence lands ≤900ms (04 §6).
     "Arming" the headline cancels the CSS gate + failsafe in one class flip;
     inline styles then own the hidden state until the spring finishes. */
  function initHeroMotion() {
    var headline = document.querySelector('[data-hero-headline]');
    if (!headline) return;
    var words = headline.querySelectorAll('[data-hero-word]');

    var gated = document.documentElement.classList.contains('bb-motion');
    if (REDUCED || !words.length || !gated) {
      // Resting state. Without the gate the words were never hidden — hiding
      // painted text now would be a blink, so we simply skip the show.
      headline.classList.add('is-armed');
      return;
    }

    // Take over from the CSS gate: inline styles hold the hidden state, then
    // .is-armed releases the stylesheet rule (and its failsafe) atomically.
    words.forEach(function (word) {
      word.style.opacity = '0';
      word.style.transform = 'translateY(0.35em)';
    });
    headline.classList.add('is-armed');

    var step = words.length > 8 ? Math.max(24, Math.floor(420 / (words.length - 1))) : 60;
    var A = motionEngine();
    if (A) {
      try {
        A.animate(words, {
          opacity: { from: 0, to: 1 },
          translateY: { from: '0.35em', to: '0em' },
          delay: A.stagger(step),
          ease: springEase(A, 80, 12),
          onComplete: function () {
            // Hand control back to the stylesheet (resting = visible).
            words.forEach(function (word) {
              word.style.opacity = '';
              word.style.transform = '';
            });
          }
        });
        return;
      } catch (err) { /* fall through to the CSS beat */ }
    }
    words.forEach(function (word, i) { cssReveal(word, i * step); });
  }

  /* Brumby peek-in — the dog photo leans in from the block edge, once per
     page load (04 §6), on the [data-brumby-peek] hook. The hidden state is
     set HERE (JS-only, element is below the fold) so no-JS, reduced-motion
     and broken-CDN visitors always get the resting state for free. The
     slight spring overshoot means Brumby leans a few px too far, then
     settles — like an actual dog checking what's on the counter. */
  function initBrumbyPeek() {
    var peeks = document.querySelectorAll('[data-brumby-peek]');
    if (!peeks.length || REDUCED || !('IntersectionObserver' in window)) return;

    function reveal(peek) {
      var A = motionEngine();
      if (A) {
        try {
          A.animate(peek, {
            opacity: { from: 0, to: 1 },
            translateX: { from: '56px', to: '0px' },
            duration: 550, // used only if the spring fallback ease kicks in
            ease: springEase(A, 120, 14),
            onComplete: function () {
              peek.style.opacity = '';
              peek.style.transform = '';
            }
          });
          return;
        } catch (err) { /* fall through */ }
      }
      cssReveal(peek, 0);
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        reveal(entry.target);
      });
    }, { threshold: 0.2, rootMargin: '0px 0px -10% 0px' });

    peeks.forEach(function (peek) {
      // The photo's -2° tilt lives on the CSS `rotate` property, so this
      // inline transform composes with it instead of clobbering it.
      peek.style.opacity = '0';
      peek.style.transform = 'translateX(56px)';
      observer.observe(peek);
    });
  }

  /* Hero load choreography — one sequenced reveal instead of everything
     simply existing when paint happens (motion pass; beats per 04 §6 clocks):
       0ms    logo pop        (scale 0.55 → 1, spring)
       200ms  box drift       (placeholder render settles down onto its tilt)
       480ms  follow-ins      (subhead → rating → CTA, 90ms stagger, fade-up)
       820ms  seal pop        (scale 0.4 → 1, spring; CSS wiggle takes over)
     Same contract as the headline words: elements hide pre-paint only under
     html.bb-motion (CSS gate + 2.5s failsafe), JS takes over with inline
     styles and flips .is-choreographed on the hero to release the gate.
     Transforms/opacity only; the box's resting −6° tilt lives on the CSS
     `rotate` property so these transforms compose with it, never clobber. */
  function initHeroChoreo() {
    var hero = document.querySelector('[data-hero]');
    if (!hero) return;

    var pops = Array.prototype.slice.call(hero.querySelectorAll('[data-hero-pop]'));
    var follows = Array.prototype.slice.call(hero.querySelectorAll('[data-hero-follow]'));
    var seal = hero.querySelector('[data-hero-seal]');
    var box = hero.querySelector('[data-hero-box]');

    var gated = document.documentElement.classList.contains('bb-motion');
    if (REDUCED || !gated) {
      // Resting state; without the gate nothing was ever hidden.
      hero.classList.add('is-choreographed');
      return;
    }

    // Take over from the CSS gate atomically (words pattern).
    pops.forEach(function (el) { el.style.opacity = '0'; el.style.transform = 'scale(0.55)'; });
    follows.forEach(function (el) { el.style.opacity = '0'; el.style.transform = 'translateY(14px)'; });
    if (seal) { seal.style.opacity = '0'; seal.style.transform = 'scale(0.4)'; }
    if (box) { box.style.opacity = '0'; box.style.transform = 'translateY(-28px)'; }
    hero.classList.add('is-choreographed');

    function clear(el) { el.style.opacity = ''; el.style.transform = ''; }

    var A = motionEngine();
    if (A) {
      try {
        if (pops.length) {
          A.animate(pops, {
            opacity: { from: 0, to: 1 },
            scale: { from: 0.55, to: 1 },
            ease: springEase(A, 120, 13),
            onComplete: function () { pops.forEach(clear); }
          });
        }
        if (box) {
          A.animate(box, {
            opacity: { from: 0, to: 1 },
            translateY: { from: '-28px', to: '0px' },
            delay: 200,
            ease: springEase(A, 90, 12),
            onComplete: function () { clear(box); }
          });
        }
        if (follows.length) {
          A.animate(follows, {
            opacity: { from: 0, to: 1 },
            translateY: { from: '14px', to: '0px' },
            delay: A.stagger(90, { start: 480 }),
            ease: springEase(A, 110, 14),
            onComplete: function () { follows.forEach(clear); }
          });
        }
        if (seal) {
          A.animate(seal, {
            opacity: { from: 0, to: 1 },
            scale: { from: 0.4, to: 1 },
            delay: 820,
            ease: springEase(A, 150, 11),
            onComplete: function () { clear(seal); }
          });
        }
        return;
      } catch (err) { /* fall through to the CSS beat */ }
    }

    // anime.js unavailable: same beats on CSS transitions.
    pops.forEach(function (el) { cssReveal(el, 0); });
    if (box) cssReveal(box, 200);
    follows.forEach(function (el, i) { cssReveal(el, 480 + i * 90); });
    if (seal) cssReveal(seal, 820);
  }

  /* ════════════════════════════════════════════════════════════
     Legacy support for sections not yet rebuilt
     ════════════════════════════════════════════════════════════ */
  function initSmoothScroll() {
    document.querySelectorAll('a[href^="#"]').forEach(function (anchor) {
      anchor.addEventListener('click', function (e) {
        var href = this.getAttribute('href');
        if (!href || href === '#') return;
        var target = document.querySelector(href);
        if (!target) return; // let the browser handle it
        e.preventDefault();
        target.scrollIntoView({ behavior: REDUCED ? 'auto' : 'smooth', block: 'start' });
      });
    });
  }

  function initializeFAQ() {
    var faqItems = document.querySelectorAll('.faq-item');
    faqItems.forEach(function (item) {
      var question = item.querySelector('.faq-question');
      var answer = item.querySelector('.faq-answer');
      var toggle = item.querySelector('.faq-toggle');
      if (!question || !answer) return;

      question.addEventListener('click', function () {
        var isActive = item.classList.contains('active');
        faqItems.forEach(function (other) {
          other.classList.remove('active');
          var q = other.querySelector('.faq-question');
          var t = other.querySelector('.faq-toggle');
          if (q) q.setAttribute('aria-expanded', 'false');
          if (t) t.textContent = '+';
        });
        if (!isActive) {
          item.classList.add('active');
          question.setAttribute('aria-expanded', 'true');
          if (toggle) toggle.textContent = '−';
        }
      });
    });
  }

  function initializeBrewingGuides() {
    document.querySelectorAll('.brewing-guide__toggle').forEach(function (toggle) {
      toggle.setAttribute('aria-expanded', 'false');
      toggle.addEventListener('click', function () {
        var guide = toggle.closest('.brewing-guide');
        if (!guide) return;
        guide.classList.toggle('active');
        toggle.setAttribute('aria-expanded', guide.classList.contains('active') ? 'true' : 'false');
      });
    });
  }

  /* ════════════════════════════════════════════════════════════
     Boot
     ════════════════════════════════════════════════════════════ */
  function init() {
    initCartDrawer();
    initQuickAdd();
    initEntrances();
    initHeroMotion();
    initHeroChoreo();
    initBrumbyPeek();
    initSmoothScroll();
    initializeFAQ();
    initializeBrewingGuides();
  }

  // PDP add-to-cart integration (IA §3.4, F-108): sections/product.liquid
  // dispatches 'cart:updated' after a successful /cart/add.js. Run the same
  // drawer path as quick-add: fetch → render → open with confirmation → bounce.
  document.addEventListener('cart:updated', function (e) {
    fetchCart().then(function (cart) {
      renderCart(cart);
      if (e.detail && e.detail.open) {
        openCartDrawer({ confirm: true });
        bounceCartBadge();
      }
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* ── Globals (legacy sections call these; keep signatures stable) ── */
  window.BrumbyBrew = Brumby;
  window.formatPrice = formatPrice;
  window.addToCart = addToCart;
  window.updateCartItem = updateCartItem;
  window.updateCartCount = updateCartCount;
  window.showNotification = showNotification;
  window.openCartDrawer = openCartDrawer;
  window.closeCartDrawer = closeCartDrawer;
})();
